import { useCallback, useEffect, useMemo } from 'react';

import { usePromoEvents } from 'common/hooks/dynamic-yield';
import { useIsObserved } from 'common/hooks/intersection-observer';
import type { CampaignTrackingData } from 'common/tracking/campaignEventData';
import type { MultipleImagesElementItem } from 'common/tracking/dispatchTrackingEventGA4';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { DyTrackingGA4Props } from 'common/tracking/getDyTrackingGA4Params';
import { getDyTrackingGA4Params } from 'common/tracking/getDyTrackingGA4Params';
import type { VisualNavigationItemData } from 'common/types/visual-navigation';

enum EventName {
  CommonVisualNavigationView = 'common-visual-navigation_view',
  CommonVisualNavigationClick = 'common-visual-navigation_click',
}

interface BaseTrackingEventsProps {
  title?: string;
  tracking?: DyTrackingGA4Props;
}

interface UseTrackingEventsProps extends BaseTrackingEventsProps {
  linkText?: string;
  linkUrl?: string;
  listPosition?: number;
  listLength?: number;
  listItem?: string;
}

export const useTrackingEvents = ({
  title,
  linkText,
  linkUrl,
  listPosition,
  listLength,
  listItem,
  tracking,
}: UseTrackingEventsProps) => {
  const { onPromoClick } = usePromoEvents((tracking ?? {}) as CampaignTrackingData);
  const dyTrackingParams = getDyTrackingGA4Params(tracking);
  const onImageClick = useCallback(() => {
    dispatchTrackingEventGA4({
      event: EventName.CommonVisualNavigationClick,
      element: 'image',
      title: title ?? '',
      list_position: listPosition,
      list_length: listLength,
      list_item: listItem,
      ...dyTrackingParams,
    });

    if (Object.keys(tracking ?? {}).length > 0) onPromoClick();
  }, [title, listPosition, listLength, listItem, dyTrackingParams]);

  const onSeeAllLinkClick = useCallback(() => {
    dispatchTrackingEventGA4({
      event: EventName.CommonVisualNavigationClick,
      element: 'link',
      title: title ?? '',
      link_text: linkText,
      link_url: linkUrl,
      ...dyTrackingParams,
    });

    if (Object.keys(tracking ?? {}).length > 0) onPromoClick();
  }, [title, linkText, linkUrl, dyTrackingParams]);

  return { onImageClick, onSeeAllLinkClick };
};

interface UseViewTrackingEventProps extends BaseTrackingEventsProps {
  items: VisualNavigationItemData[];
  activeSlide: number;
  slidesToShow: number;
}

export const useViewTrackingEvent = ({
  title,
  items,
  activeSlide,
  slidesToShow,
  tracking,
}: UseViewTrackingEventProps) => {
  const [ref, isObserved] = useIsObserved();
  const { onPromoView } = usePromoEvents((tracking ?? {}) as CampaignTrackingData);
  const shownItemsIndex = useMemo(() => new Set<number>(), [items]);

  useEffect(() => {
    if (isObserved) {
      const newItemsToTrack: MultipleImagesElementItem[] = [];
      items.slice(activeSlide, activeSlide + (slidesToShow + 1)).forEach((item, index) => {
        if (!shownItemsIndex.has(items.indexOf(item))) {
          newItemsToTrack.push({
            list_position: index + activeSlide,
            list_item: item.description,
          });

          shownItemsIndex.add(index + activeSlide);
        }
      });

      if (newItemsToTrack.length) {
        dispatchTrackingEventGA4({
          event: EventName.CommonVisualNavigationView,
          element: 'image',
          title: title ?? '',
          list_length: items.length,
          items: newItemsToTrack,
          ...getDyTrackingGA4Params(tracking),
        });

        if (Object.keys(tracking ?? {}).length > 0) onPromoView();
      }
    }
  }, [activeSlide, slidesToShow, items, tracking, isObserved]);

  return { ref };
};
